import { Badge } from "@/components/ui/badge";
import { Lock, ExternalLink } from "lucide-react";

export function fmtAmount(v, currency = "") {
  if (v == null || v === "") return "—";
  const n = Number(v);
  const s = n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${s}` : s;
}

export function fmtAmountRaw(v) {
  if (v == null || v === "") return "—";
  return Number(v).toLocaleString("en-US", { maximumFractionDigits: 8 });
}

export function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return v;
  }
}

function trunc(v, n = 14) {
  if (!v) return "—";
  const s = String(v);
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  broadcast: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border-cyan-500/30",
  requires_action: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  suspended: "bg-red-500/15 text-red-300 border-red-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
  inactive: "bg-muted text-muted-foreground border-border",
  revoked: "bg-red-500/15 text-red-300 border-red-500/30",
};

const ROLE_STYLES = {
  admin: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  staff: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  company: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  customer: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export function renderCell(col, row) {
  const v = row[col.key];
  switch (col.render) {
    case "amount":
      return <span className="font-mono text-foreground">{fmtAmount(v, row.currency)}</span>;
    case "amountRaw":
      return <span className="font-mono text-foreground">{fmtAmountRaw(v)}</span>;
    case "pct":
      return <span className="font-mono">{v}%</span>;
    case "rateUsage": {
      const limit = Number(v || 0);
      const recent = Number(row.recent_calls || 0);
      const pct = limit > 0 ? Math.min(100, Math.round((recent / limit) * 100)) : 0;
      const level = pct >= 100 ? "crit" : pct >= 80 ? "warn" : "ok";
      const styles = {
        ok: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30",
        warn: "bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/30",
        crit: "bg-red-500/15 text-red-600 dark:text-red-300 border-red-500/30",
      };
      return (
        <div className="flex items-center gap-2" data-testid="rate-usage-cell">
          <span className="font-mono text-xs text-muted-foreground">{recent}/{limit}<span className="opacity-60">·min</span></span>
          <Badge className={`${styles[level]} font-mono text-[10px] px-1.5 py-0`}>{pct}%</Badge>
        </div>
      );
    }
    case "txHashLink":
      if (!v) return <span className="text-muted-foreground">—</span>;
      return row.explorer_url ? (
        <a href={row.explorer_url} target="_blank" rel="noopener noreferrer" data-testid="tx-explorer-link"
          className="font-mono text-xs text-indigo-500 hover:text-indigo-400 hover:underline inline-flex items-center gap-1">
          {trunc(v, 18)} <ExternalLink className="h-3 w-3" />
        </a>
      ) : <span className="font-mono text-xs text-foreground" title={v}>{trunc(v, 18)}</span>;
    case "url":
      return v ? <span className="font-mono text-xs text-foreground break-all">{v}</span> : <span className="text-muted-foreground">—</span>;
    case "lockedSecret":
      return v
        ? <span className="font-mono text-xs text-amber-300/90 inline-flex items-center gap-1"><Lock className="h-3 w-3" /> ••••••••</span>
        : <span className="text-muted-foreground">—</span>;
    case "date":
      return <span className="text-muted-foreground text-xs">{fmtDate(v)}</span>;
    case "bool":
      return v ? (
        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">On</Badge>
      ) : (
        <Badge className="bg-muted text-muted-foreground border-border">Off</Badge>
      );
    case "statusBadge":
      return <Badge className={STATUS_STYLES[v] || STATUS_STYLES.inactive}>{v || "—"}</Badge>;
    case "processorStatus":
      return <Badge className={STATUS_STYLES[v] || STATUS_STYLES.inactive}>{v}</Badge>;
    case "roleBadge":
      return <Badge className={ROLE_STYLES[v] || ROLE_STYLES.customer}>{v}</Badge>;
    case "capBadge":
      return <Badge className="bg-muted text-foreground border-border capitalize">{v || "—"}</Badge>;
    default:
      if (col.mono) return <span className="font-mono text-xs text-foreground">{trunc(v)}</span>;
      return <span className="text-foreground">{v ?? "—"}</span>;
  }
}
